import React, { FC, useEffect, useState } from 'react';
import Timeline from '../components/Timeline';
import { TimelineEvent } from '../components/Timeline';
import { ReactComponent as FormEditIcon } from 'assets/icons/FormEditIcon.svg';
import FormCloseReactIcon from 'assets/react-icons/FormCloseReactIcon';
import NoteModal from 'components/note-modal/NoteModal';
import ActionsFilter from './components/ActionsFilter';
import LineItemPlusReactIcon from 'assets/react-icons/LineItemPlusReactIcon';
import { useLazyGetPayRunMemberHistoryQuery } from './api';
import {
  useCreatePayRunNoteMutation,
  useDeletePayRunNoteMutation,
  useUpdatePayRunNoteMutation
} from '../pay-period-review/api';
import { useParams } from 'react-router-dom';

import { selectCurrentTeam } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks/typedHooks';
import { Loader } from 'components';
import { logAnalyticEvent } from 'utils/analytics';
import { PayRunsCleverTapEvents } from '../events';
const MemberHistoryTab: FC = () => {
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<TimelineEvent[]>([]);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [note, setNote] = useState<string>('');

  // Get params from URL and current team
  const { payRunId, merchantId } = useParams<{ payRunId: string; merchantId: string }>();
  const currentTeam = useAppSelector(selectCurrentTeam);
  const teamId = currentTeam?.id;

  // API query hooks
  const [getPayRunMemberHistory, { data: historyData, isLoading }] = useLazyGetPayRunMemberHistoryQuery();
  const [createPayRunNote, { isLoading: isCreatingNote }] = useCreatePayRunNoteMutation();
  const [updatePayRunNote, { isLoading: isUpdatingNote }] = useUpdatePayRunNoteMutation();
  const [deletePayRunNote, { isLoading: isDeletingNote }] = useDeletePayRunNoteMutation();

  // Fetch history on component mount
  useEffect(() => {
    if (teamId && payRunId && merchantId) {
      getPayRunMemberHistory({ teamId, payRunId, merchantId });
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberHistoryView, {
        payRunId,
        merchantId
      });
    }
  }, [teamId, payRunId, merchantId, getPayRunMemberHistory]);

  // Set timeline events from API response
  useEffect(() => {
    if (historyData?.events) {
      const timelineEvents = historyData.events as TimelineEvent[];
      if (selectedActions.length === 0) {
        setFilteredEvents(timelineEvents);
      } else {
        setFilteredEvents(
          timelineEvents.filter(event => {
            return selectedActions.includes(event.event);
          })
        );
      }

      // Set note if available in the response
      if (historyData.notes) {
        setNote(historyData.notes);
      }
    }
  }, [historyData, selectedActions]);

  const handleActionsChange = (actions: string[]) => {
    setSelectedActions(actions);
  };

  const handleOpenNoteModal = (edit = false) => {
    setIsEditMode(edit);
    setIsNoteModalOpen(true);
  };

  // Handle functions for note operations
  const handleAddNote = async (noteText: string) => {
    if (!teamId || !payRunId || !merchantId) return;

    try {
      await createPayRunNote({ teamId, payRunId, merchantId, note: noteText });
      setNote(noteText);
      setIsNoteModalOpen(false);
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberHistoryNoteAction, {
        payRunId,
        merchantId,
        action: 'add'
      });
    } catch (error) {
      //error
    }
  };

  const handleEditNote = async (noteText: string) => {
    if (!teamId || !payRunId || !merchantId) return;

    try {
      await updatePayRunNote({ teamId, payRunId, merchantId, note: noteText });
      setNote(noteText);
      setIsNoteModalOpen(false);
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberHistoryNoteAction, {
        payRunId,
        merchantId,
        action: 'edit'
      });
    } catch (error) {
      //error
    }
  };

  const handleDeleteNote = async () => {
    if (!teamId || !payRunId || !merchantId) return;

    try {
      await deletePayRunNote({ teamId, payRunId, merchantId });
      setNote('');
      logAnalyticEvent(PayRunsCleverTapEvents.webPayRunMemberHistoryNoteAction, {
        payRunId,
        merchantId,
        action: 'delete'
      });
    } catch (error) {
      //error
    }
  };

  const handleSaveNote = async (noteText: string) => {
    if (isEditMode) {
      await handleEditNote(noteText);
    } else {
      await handleAddNote(noteText);
    }
  };

  // Combine loading states for the modal
  const isNoteOperationLoading = isCreatingNote || isUpdatingNote || isDeletingNote;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <div className="flex h-full flex-col items-center overflow-hidden rounded-lg bg-white p-4">
      {/* Notes Section */}
      <div className="mx-auto w-[773px] max-w-full">
        <h3 className="mb-2 text-17px font-bold text-primaryText">Note</h3>
        <div className="relative items-center rounded-md border border-borderGray p-4">
          {note ? (
            <>
              <p className="pr-16 text-base text-primaryText">{note}</p>
              <div className="absolute top-3 right-3 flex gap-2">
                <div
                  className="cursor-pointer"
                  onClick={() => handleOpenNoteModal(true)}
                  id="history-tab-edit-note-button">
                  <FormEditIcon className="h-5 w-5" />
                </div>
                <div className="cursor-pointer" onClick={handleDeleteNote} id="history-tab-delete-note-button">
                  <FormCloseReactIcon id="history-tab-delete-note-button" className="h-5 w-5" />
                </div>
              </div>
            </>
          ) : (
            <div className="flex h-full items-center">
              <button
                onClick={() => handleOpenNoteModal(false)}
                className="flex items-center text-secondary"
                id="history-tab-add-note-button">
                <LineItemPlusReactIcon id="history-tab-add-note-button" className="mr-2 h-5 w-5" />
                <span className="text-17px font-semibold">Add a member note</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Actions Filter Section */}
      <div className="mx-auto mt-6 w-[773px] max-w-full">
        <h3 className="mb-2 text-17px font-bold text-primaryText">History</h3>
        <ActionsFilter selectedActions={selectedActions} onSelectActions={handleActionsChange} />
        <hr className="my-4 w-full border-secondaryBtn" />
        {/* Timeline Section */}
        <div className="flex h-full flex-1 overflow-hidden">
          {filteredEvents.length > 0 ? (
            <Timeline
              events={filteredEvents}
              timelineId="pay-run-history-timeline"
              onEditNoteClick={event => {
                if (event.event === 'NOTE') {
                  handleOpenNoteModal(true);
                }
              }}
              onDeleteNoteClick={event => {
                if (event.event === 'NOTE' && handleDeleteNote) {
                  handleDeleteNote();
                }
              }}
            />
          ) : (
            <div className="flex h-32 items-center justify-center text-accentText">No events to display</div>
          )}
        </div>
      </div>

      {/* Note Modal */}
      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSave={handleSaveNote}
        defaultNote={isEditMode ? note : ''}
        isEdit={isEditMode}
        title={isEditMode ? 'Edit member note' : 'Add member note'}
        placeholder="Enter note here"
        saveButtonText={'Save'}
        isLoading={isNoteOperationLoading}
      />
    </div>
  );
};

export default MemberHistoryTab;
